const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const EDGE_BASE = `${SUPABASE_URL}/functions/v1`;

/**
 * Kennung dieser Website. Das Backend wird von beiden Seiten
 * (balivwtour.com und villakompiangbali.com) genutzt und waehlt anhand
 * dieser Kennung Marke, Absenderadresse und Links in den Mails aus.
 */
const SITE_KEY = "balivwtour";

type Result<T> = { data: T | null; error: Error | null };

type ReservationRow = { check_in: string; check_out: string };
type BlockedRow = { blocked_date: string };

export type CreateReservationParams = {
  p_villa_slug: string;
  p_check_in: string;
  p_check_out: string;
  p_adults: number;
  p_children: number;
  p_firstname: string;
  p_lastname: string;
  p_email: string;
  p_phone: string | null;
  p_nationality: string | null;
  p_message: string | null;
};

async function postgrestRpc<T>(
  fn: string,
  params: Record<string, unknown>
): Promise<Result<T>> {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(params),
    });

    if (!res.ok) {
      let message = res.statusText || `HTTP ${res.status}`;
      try {
        const errBody = (await res.json()) as {
          message?: string;
          details?: string;
          hint?: string;
        };
        message = errBody?.message || errBody?.details || errBody?.hint || message;
      } catch {
        // ignore body parse errors, keep status text
      }
      return { data: null, error: new Error(message) };
    }

    const data = (await res.json()) as T;
    return { data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error("network error"),
    };
  }
}

async function invokeEdgeFunction<T>(
  functionName: string,
  body: Record<string, unknown>
): Promise<Result<T>> {
  try {
    const res = await fetch(`${EDGE_BASE}/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ ...body, site: SITE_KEY }),
    });

    const responseText = await res.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(responseText);
    } catch {
      parsed = responseText;
    }

    if (!res.ok) {
      const msg =
        (parsed as Record<string, string>)?.message ||
        (parsed as Record<string, string>)?.error ||
        responseText ||
        `HTTP ${res.status}`;
      return { data: null, error: new Error(msg) };
    }

    return { data: parsed as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error("network error"),
    };
  }
}

export const getVillaAvailability = (slug: string): Promise<Result<ReservationRow[]>> =>
  postgrestRpc<ReservationRow[]>("get_villa_availability", { p_villa_slug: slug });

export const getVillaBlockedDates = (slug: string): Promise<Result<BlockedRow[]>> =>
  postgrestRpc<BlockedRow[]>("get_villa_blocked_dates", { p_villa_slug: slug });

export type TourBlockedRow = { blocked_date: string };

/** Öffentlich lesbare Liste der gesperrten Tour-Tage (globale Tour-Sperre). */
export const getTourBlockedDates = (): Promise<Result<TourBlockedRow[]>> =>
  postgrestRpc<TourBlockedRow[]>("get_tour_blocked_dates", {});

export const createVillaReservation = (
  params: CreateReservationParams
): Promise<Result<string>> =>
  postgrestRpc<string>("create_villa_reservation", params);

export type EdgeReservationBody = {
  villa_slug: string;
  check_in: string;
  check_out: string;
  adults: number;
  rooms: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string | null;
  nationality: string | null;
  message: string | null;
  lang: string;
};

export type EdgeReservationResponse = {
  ok: true;
  id: string;
  emailStatus?: string;
  emailDetails?: { label: string; to: string }[];
} | {
  error: string;
  message: string;
};

export const createVillaReservationViaEdge = (
  body: EdgeReservationBody
): Promise<Result<EdgeReservationResponse>> =>
  invokeEdgeFunction<EdgeReservationResponse>("villa-reservation", body);

export type WeddingInquiryBody = {
  wedding_type: string;
  wedding_date: string;
  guests: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string | null;
  message: string | null;
  lang: string;
};

export type WeddingInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitWeddingInquiry = (
  body: WeddingInquiryBody
): Promise<Result<WeddingInquiryResponse>> =>
  invokeEdgeFunction<WeddingInquiryResponse>("wedding-inquiry", body);

export type ContactInquiryBody = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string | null;
  lang: string;
};

export type ContactInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitContactInquiry = (
  body: ContactInquiryBody
): Promise<Result<ContactInquiryResponse>> =>
  invokeEdgeFunction<ContactInquiryResponse>("contact-inquiry", body);

export type DayTripInquiryBody = {
  tour: string;
  arrival: string;
  date: string;
  adults: string;
  children: string;
  hotel: string;
  operator: string | null;
  lastname: string;
  firstname: string;
  nationality: string;
  guidelang: string;
  email: string;
  phone: string | null;
  message: string | null;
  lang: string;
};

export type DayTripInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitDayTripInquiry = (
  body: DayTripInquiryBody
): Promise<Result<DayTripInquiryResponse>> =>
  invokeEdgeFunction<DayTripInquiryResponse>("daytrip-inquiry", body);

export type MultidayInquiryBody = {
  tour: string;
  arrival: string;
  start: string;
  end: string;
  adults: string;
  children: string;
  hotel: string;
  operator: string | null;
  lastname: string;
  firstname: string;
  nationality: string;
  guidelang: string;
  email: string;
  phone: string | null;
  message: string | null;
  lang: string;
};

export type MultidayInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitMultidayInquiry = (
  body: MultidayInquiryBody
): Promise<Result<MultidayInquiryResponse>> =>
  invokeEdgeFunction<MultidayInquiryResponse>("multiday-inquiry", body);

export type AdventureInquiryBody = {
  activity: string;
  date: string;
  adults: string;
  children: string;
  hotel: string;
  operator: string | null;
  lastname: string;
  firstname: string;
  nationality: string;
  email: string;
  phone: string | null;
  message: string | null;
  lang: string;
};

export type AdventureInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitAdventureInquiry = (
  body: AdventureInquiryBody
): Promise<Result<AdventureInquiryResponse>> =>
  invokeEdgeFunction<AdventureInquiryResponse>("adventure-inquiry", body);

export type BausteinPrice = {
  key: string;
  category: string;
  label_de: string;
  label_en: string;
  amount: number;
  amount_eur: number | null;
  unit: string;
  sort_order: number;
};

export const getBausteinPrices = async (): Promise<Result<BausteinPrice[]>> => {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/baustein_prices?select=*&order=sort_order.asc`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
        },
      }
    );

    if (!res.ok) {
      let message = res.statusText || `HTTP ${res.status}`;
      try {
        const errBody = (await res.json()) as {
          message?: string;
          details?: string;
          hint?: string;
        };
        message = errBody?.message || errBody?.details || errBody?.hint || message;
      } catch {
        // ignore body parse errors, keep status text
      }
      return { data: null, error: new Error(message) };
    }

    const data = (await res.json()) as BausteinPrice[];
    return { data: data ?? [], error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error("network error"),
    };
  }
};

export type GalleryCaptionRow = { image_key: string; caption: string };

export const getGalleryCaptions = async (
  galleryKey: string
): Promise<Result<GalleryCaptionRow[]>> => {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/gallery_captions?select=image_key,caption&gallery_key=eq.${encodeURIComponent(galleryKey)}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
        },
      }
    );

    if (!res.ok) {
      let message = res.statusText || `HTTP ${res.status}`;
      try {
        const errBody = (await res.json()) as {
          message?: string;
          details?: string;
          hint?: string;
        };
        message = errBody?.message || errBody?.details || errBody?.hint || message;
      } catch {
        // ignore body parse errors, keep status text
      }
      return { data: null, error: new Error(message) };
    }

    const data = (await res.json()) as GalleryCaptionRow[];
    return { data: data ?? [], error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error("network error"),
    };
  }
};

export type BausteinInquiryBody = {
  villa: string;
  villa_slug: string;
  start: string;
  end: string;
  persons: number;
  excursions: {
    day_trip: { days: number; persons: number };
    massage: { days: number; persons: number };
    dinner: { days: number; persons: number };
    rafting: number;
    sailing: number;
  };
  arrival_transfer: string;
  departure_transfer: string;
  arrival_flight: { airline: string | null; number: string | null; time: string | null } | null;
  departure_flight: { airline: string | null; number: string | null; time: string | null } | null;
  arrival_pickup: string | null;
  departure_pickup: string | null;
  arrival_pickup_time: string | null;
  departure_pickup_time: string | null;
  firstname: string;
  lastname: string;
  nationality: string | null;
  email: string;
  phone: string | null;
  message: string | null;
  pre_program_wish: string | null;
  post_program_wish: string | null;
  lang: string;
};

export type BausteinInquiryResponse = {
  ok: true;
  emailStatus?: string;
} | {
  error: string;
  message: string;
};

export const submitBausteinInquiry = (
  body: BausteinInquiryBody
): Promise<Result<BausteinInquiryResponse>> =>
  invokeEdgeFunction<BausteinInquiryResponse>("baustein-inquiry", body);

export type VillaGalleryFile = {
  name: string;
  url: string;
  updatedAt: string | null;
};

/**
 * Liest 1:1 die Dateien aus dem Supabase-Ordner "villas/villa-gallery"
 * (jede Datei genau einmal, in Dateinamen-Reihenfolge).
 */
export const getVillaGalleryFiles = async (): Promise<Result<VillaGalleryFile[]>> => {
  try {
    const res = await fetch(`${EDGE_BASE}/villa-gallery-list`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({}),
    });

    const responseText = await res.text();
    let parsed: { ok?: boolean; images?: VillaGalleryFile[]; error?: string } | null = null;
    try {
      parsed = JSON.parse(responseText) as typeof parsed;
    } catch {
      parsed = null;
    }

    if (!res.ok || !parsed?.ok || !Array.isArray(parsed.images)) {
      return {
        data: null,
        error: new Error(parsed?.error || responseText || `HTTP ${res.status}`),
      };
    }

    return { data: parsed.images, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error("network error"),
    };
  }
};